import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  Tooltip,
  Collapse,
  TablePagination,
} from '@mui/material';

import {
    Delete,
    Edit,
    FileUpload,
  LocationOn,
  Person,
  Search,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { LoadLocation, LoadLocationForm, Material, Meta } from '../../types';
import { loadLocationApi, materialApi } from '../../services/api';
import LocationFormModal from '../../components/AddNewLoadLocationPopUp';
import { unwrapListResponse, unwrapMetaResponse } from '../../apiUnwrapper/apiUnwrapper';
import { useSnackbar } from 'notistack';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

const LoadLocationsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageLocations = user?.roleName === 'Admin';
  const [open, setOpen] = useState(false);

  const [loadLocations, setLoadLocations] = useState<LoadLocation[]>([]);
  const [loadLocationsMeta, setLoadLocationsMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileName, setFileName] = useState('');
  const [openMaterialTonnage, setOpenMaterialTonnage] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [selectedLocation, setSelectedLocation] = useState<LoadLocationForm | undefined>(undefined);
  const { enqueueSnackbar } = useSnackbar();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
  const [confirmMessage, setConfirmMessage] = useState("Are you sure?");

  const openCreateModal = () => {
    setMode("create");
    setSelectedLocation({
      level1: "",
      level2: "",
      level3: "",
      level4: "",
      isActive: true,
      materialsTonnage: []
    });
    setOpen(true);
  };

  const openEditModal = (loc: LoadLocation) => {
    setMode("edit");
    setSelectedLocation({
      id: loc.id,
      level1: loc.level1,
      level2: loc.level2,
      level3: loc.level3,
      level4: loc.level4,
      isActive: loc.isActive,
      materialsTonnage: loc.materialsTonnage.map(mt => ({
        materialId: mt.materialId,
        tonnage: mt.tonnage
      }))
    });
    setOpen(true);
  };

  const handleSubmit = async (data: LoadLocationForm) => {
    try{
      if (mode === "create") {
        await loadLocationApi.createLoadLocation(data);
      } else {
        await loadLocationApi.updateLoadLocation(data);
      }
      enqueueSnackbar(`Load Location ${mode === "create" ? "created" : "updated"} successfully`, { variant: 'success' });
      await reloadlocations(loadLocationsMeta.page, loadLocationsMeta.size);
    } catch (err) {
      enqueueSnackbar(`Failed to ${mode === "create" ? "create" : "update"} Load Location`, { variant: 'error' });
      console.error(`Error ${mode === "create" ? "creating" : "updating"} Load Location:`, err);
    } finally { 
      setOpen(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setFileName(file.name);
        // You can also upload or parse the file here
      }
  };
  useEffect(() => {
      reloadlocations(loadLocationsMeta.page, loadLocationsMeta.size);
   }, []);
        
          const reloadlocations = async (page = 1, size = 5) => {
            try {
              setLoading(true);
              setError(null);
              const loadLocationsData = await loadLocationApi.getLoadLocations(page, size);
              setLoadLocations(unwrapListResponse<LoadLocation>(loadLocationsData));
              setMaterials(await materialApi.geActivetMaterials());
              setLoadLocationsMeta(unwrapMetaResponse<Meta>(loadLocationsData) || { total: 0, size: 5, page: 1 });
            } catch (err) {
              setError('Failed to load load locations. Make sure the Spring Boot backend is running on port 8080.');
              console.error('Error loading load locations:', err);
            } finally {
              setLoading(false);
            }
          };

          const filteredLoadLocations = loadLocations.filter(loadLocation =>
            loadLocation.level1.toLowerCase().includes(searchTerm.toLowerCase()) ||
            loadLocation.level2.toLowerCase().includes(searchTerm.toLowerCase()) ||
            loadLocation.level3.toLowerCase().includes(searchTerm.toLowerCase()) ||
            loadLocation.level4.toLowerCase().includes(searchTerm.toLowerCase())
          );
  function handleDelete(id: number): void {
    try{
              setConfirmAction(() => async () => {
              await loadLocationApi.deleteLoadLocation(id);
              enqueueSnackbar("Load Location deleted successfully", { variant: "success" });
              reloadlocations(loadLocationsMeta.page, loadLocationsMeta.size);
            });
            setConfirmMessage("Are you sure you want to delete this Load Location?");
            setOpenConfirm(true);
          }
          catch(error){
            enqueueSnackbar("Error deleting Load Location: "+error , { variant: "error" });
            setError('Error deleting Load Location: '+error );
          }
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
      {/* Main Content for Load Locations Page can be added here */}
      <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
      <PageHeader
                                        title="Load Locations Management"
                                        Icon={LocationOn}
                                        onBack={() => navigate('/haulmasterlive')}
                                        backLabel="Back to Haul Master"
                                        subtitle="Manage loading sites and materials availability"
                                      />

        {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, border: '1px solid #ef4444' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search operators by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
                sx: { backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }
              }}
              sx={{ 
                flexGrow: 1, 
                minWidth: 250,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: '#64748b' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' },
                },
                '& .MuiInputBase-input': { color: theme.palette.text.primary }
              }}
            />
            {canManageLocations && (
              <>
            <Button variant="contained" sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
              }} onClick={() => openCreateModal()}>Add New Load Location</Button>
            <LocationFormModal open={open} onClose={() => setOpen(false)} onSubmit={handleSubmit} mode={mode} materials={materials} initialData={selectedLocation} />


      <Button
        variant="contained"
        component="label"
        startIcon={<FileUpload />}
        sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
              }}
      >
        Bulk Upload
        <input
          type="file"
          accept=".csv"
          hidden
          onChange={handleFileChange}
        />
      </Button>
      {fileName && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Selected file: <strong>{fileName}</strong>
        </Typography>
      )}
              </>
            )}

          </Box>
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Load Locations ({loadLocationsMeta.total})
          </Typography>
          <TablePagination
                                component="div"
                                count={loadLocationsMeta.total}
                                page={loadLocationsMeta.page - 1}
                                onPageChange={(e, newPage) => reloadlocations(newPage + 1, loadLocationsMeta.size)}
                                rowsPerPage={loadLocationsMeta.size}
                                onRowsPerPageChange={(e) => reloadlocations(1, parseInt(e.target.value, 10))}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                labelRowsPerPage="Load Locations per page"
                              />
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#2d3e50' }}>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 1</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 2</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 3</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Level 4</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Materials and Tonnage</TableCell>
                  <TableCell sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Status</TableCell>
                  {canManageLocations && (
                    <TableCell align="center" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLoadLocations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canManageLocations ? 7 : 6} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Person sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                          No Load locations found
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {searchTerm ? 'Try adjusting your search terms' : 'Start the Spring Boot backend to load operators'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLoadLocations.map((loadLocation) => (
                    <TableRow key={loadLocation.id} hover sx={{ '&:hover': { backgroundColor: theme.palette.background.paper }, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{loadLocation.level1}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{loadLocation.level2}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{loadLocation.level3}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary }}>{loadLocation.level4}</Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                            <Tooltip
                              title={
                                <Box>
                                  {loadLocation.materialsTonnage?.map((mt) => {
                                    return (
                                      <Typography key={mt.id} variant="body2">
                                        {mt.materialName}: {mt.tonnage} T
                                      </Typography>
                                    );
                                  })}
                                </Box>
                              }
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ cursor: 'pointer', color: theme.palette.text.primary }}
                                onClick={() => setOpenMaterialTonnage(!openMaterialTonnage)}
                              >
                                {loadLocation.materialsTonnage.length} materials
                              </Typography>
                            </Tooltip>

                            <Collapse in={openMaterialTonnage} timeout="auto" unmountOnExit>
                              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {loadLocation.materialsTonnage?.map((mt) => {

                                  return (
                                    <Typography
                                      key={mt.id}
                                      variant="body2"
                                      sx={{ color: theme.palette.text.secondary }}
                                    >
                                      • {mt.materialName}: <strong>{mt.tonnage} T</strong>
                                    </Typography>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </TableCell>
                      <TableCell sx={{ borderBottom: 'none' }}>
                          <Chip label={loadLocation.isActive? "Active" : "Inactive"} size="small" />
                      </TableCell>
                      {canManageLocations && (
                      <TableCell align="center" sx={{ borderBottom: 'none' }}>
                                              <IconButton size="small" sx={{ color: '#06beb6' }}
                                                onClick={() => {
                                                openEditModal(loadLocation);
                                                }}>
                                              <Edit />
                                              </IconButton>
                                              <IconButton
                                                key={loadLocation.id}
                                                size="small"
                                                sx={{ color: '#ef4444' }}
                                                onClick={() => handleDelete(loadLocation.id)} // ✅ pass unit.id
                                              >
                                                <Delete />
                                              </IconButton>
                                            </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
                                component="div"
                                count={loadLocationsMeta.total}
                                page={loadLocationsMeta.page - 1}
                                onPageChange={(e, newPage) => reloadlocations(newPage + 1, loadLocationsMeta.size)}
                                rowsPerPage={loadLocationsMeta.size}
                                onRowsPerPageChange={(e) => reloadlocations(1, parseInt(e.target.value, 10))}
                                rowsPerPageOptions={[5, 10, 15, 20]}
                                labelRowsPerPage="Load Locations per page"
                              />
          <ConfirmDialog
                              open={openConfirm}
                              onClose={() => setOpenConfirm(false)}
                              onConfirm={confirmAction}
                              title="Confirm Delete"
                              message={confirmMessage}
                            />
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
};

export default LoadLocationsPage;